/* =============================================================================
   Formel -> HTML

   Ausgegeben wird ein Kasten der Höhe 0, der auf der Grundlinie sitzt (wie die
   Zeichen in A4Page). Darin liegen
     - die Zeichen als absolut gesetzte <span data-gn-ch> in der Schrift der
       Seite. Dadurch bleiben Strichstärke-Regler, Farbe und der GoodNotes-
       Export (der genau diese Spans liest) unverändert gültig.
     - die Striche als kurze, gedrehte Balken mit runden Enden. Bewusst kein
       SVG und kein Canvas: html2canvas zeichnet Hintergründe, Rundungen und
       Drehungen scharf in jeder Auflösung, und beim Bild-Export wird die Seite
       per cloneNode kopiert — ein Canvas käme dabei leer an.

   Höhe: Passt eine Formel nicht zwischen die Linien (Doppelbruch, Wurzel über
   Bruch), bekommt die Zeile genau eine (oder mehrere) Linien mehr. So bleibt
   der Text darunter auf den Linien des Papiers.

   Export: html2canvas setzt Text etwas tiefer als der Browser, und zwar
   proportional zur Schriftgrösse (schriftMetrik.js). Home.js gleicht das für
   den ganzen Textkasten aus; kleinere Zeichen in Brüchen und die Striche
   bekommen hier den Rest der Korrektur.
   ========================================================================== */

import { leseFormel } from "./parser";
import { setzeFormel } from "./layout";
import { matheMetrik } from "./metrik";

const layoutMerker = new Map();

const rund = (v) => Math.round(v * 100) / 100;

function zufall(n) {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function esc(ch) {
  if (ch === "<") return "&lt;";
  if (ch === ">") return "&gt;";
  if (ch === "&") return "&amp;";
  if (ch === '"') return "&quot;";
  return ch;
}

const BRUCHZEILE = "\uE003"; // siehe normalisieren.js

function layoutFuer(quelle, opt, M) {
  const lw = opt.laufweite ? opt.laufweite(opt.S) : 0;
  const schluessel = `${opt.stapel}|${opt.S}|${opt.dicke}|${rund(lw)}|${M.xh}|${quelle}`;
  if (layoutMerker.has(schluessel)) return layoutMerker.get(schluessel);
  const bruchzeile = quelle.startsWith(BRUCHZEILE);
  const box = setzeFormel(leseFormel(bruchzeile ? quelle.slice(1) : quelle), {
    bruchzeile,
    metrik: M,
    S: opt.S,
    dicke: opt.dicke,
    laufweite: (s) => (opt.laufweite ? opt.laufweite(opt.S) * (s / opt.S) : 0),
  });
  // Solange die Schrift (oder ihre Ersatzschrift) noch lädt, fehlen Zeichen
  // in der Messung — so ein Satz darf nicht hängen bleiben.
  if (!M.geladen) return box;
  if (layoutMerker.size > 800) layoutMerker.clear();
  layoutMerker.set(schluessel, box);
  return box;
}

/* Striche leicht verwackeln — wie von Hand gezogen, nicht gedruckt. Der Zufall
   hängt am Zeichenindex, „Neue Variation“ ändert ihn also mit. */
function verwackle(striche, u, unordnung, saat) {
  const amp = u * (0.025 + 0.0007 * unordnung);
  const winkelMax = ((0.7 + 0.05 * unordnung) * Math.PI) / 180;
  return striche.map((s, si) => {
    const p = s.p.slice();
    const n = p.length / 2;
    let cx = 0;
    let cy = 0;
    for (let i = 0; i < n; i++) {
      cx += p[i * 2];
      cy += p[i * 2 + 1];
    }
    cx /= n;
    cy /= n;
    // Bruchstriche auf der Papierlinie bleiben waagrecht, sonst lugt die
    // Linie an einem Ende darunter hervor
    const w = s.gerade ? 0 : (zufall(saat + si * 7.1) - 0.5) * 2 * winkelMax;
    const cos = Math.cos(w);
    const sin = Math.sin(w);
    for (let i = 0; i < n; i++) {
      const dx = p[i * 2] - cx;
      const dy = p[i * 2 + 1] - cy;
      const ruhig = n <= 1 || s.glatt;
      const jx = ruhig ? 0 : (zufall(saat + si * 13.3 + i * 1.7) - 0.5) * 2 * amp;
      const jy = ruhig || s.gerade ? 0 : (zufall(saat + si * 17.9 + i * 2.3) - 0.5) * 2 * amp;
      p[i * 2] = cx + dx * cos - dy * sin + jx;
      p[i * 2 + 1] = cy + dx * sin + dy * cos + jy;
    }
    return { p, d: s.d };
  });
}

function balken(x1, y1, x2, y2, d, farbe) {
  const len = Math.hypot(x2 - x1, y2 - y1);
  const winkel = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
  const r = d / 2;
  return (
    `<span style="position:absolute;left:${rund(x1 - r)}px;top:${rund(y1 - r)}px;` +
    `width:${rund(len + d)}px;height:${rund(d)}px;border-radius:${rund(r)}px;` +
    `background:${farbe};transform-origin:${rund(r)}px ${rund(r)}px;` +
    `transform:rotate(${rund(winkel)}deg)"></span>`
  );
}

/* opt:
     stapel      font-family der Seite
     S           Schriftgrösse der Seite in px
     laufweite   (s) -> letter-spacing in px, wie bei den normalen Zeichen
     dicke       Strichstärke in px bei Grundgrösse
     farbe       Textfarbe
     unordnung   Regler „Unordentlichkeit“ (0–100)
     variation   (i) -> { sizeVariation, yOffset } wie getCharVariation
     index       Zeichenindex der Formel im Text
     zeile       { L, a } Zeilenabstand und Lage der Grundlinie, oder null
     export      { V0, S0 } Korrektur für html2canvas, oder null           */
export function formelHTML(quelle, opt) {
  const M = matheMetrik(opt.stapel);
  const box = layoutFuer(quelle, opt, M);
  const S = opt.S;
  const u = M.xh * S;
  const unordnung = opt.unordnung || 0;
  const index = opt.index || 0;
  const farbe = opt.farbe || "#000";

  const korrText = (s) => (opt.export ? opt.export.V0 * (1 - s / opt.export.S0) : 0);
  const korrStrich = opt.export ? opt.export.V0 : 0;

  let html = "";

  box.glyphen.forEach((gl, i) => {
    const v = opt.variation ? opt.variation(index + i) : { sizeVariation: 0, yOffset: 0 };
    const s = gl.s * (1 + v.sizeVariation);
    const anteil = gl.s / S;
    const jx = (zufall((index + i) * 13.37) - 0.5) * 2 * (unordnung / 100) * 0.6;
    const jy = v.yOffset * (unordnung / 10) * anteil;
    // gl.y enthält die Absenkung der Ersatzschrift (gl.ab) für die Nenngrösse;
    // sie wächst mit der Zufallsgrösse mit
    const ab = gl.ab || 0;
    const top = -(gl.y + ab * gl.s - ab * s) - M.r * s + korrText(s);
    html +=
      `<span data-gn-ch="${esc(gl.ch)}" style="position:absolute;left:${rund(gl.x)}px;top:${rund(top)}px;` +
      `font-size:${rund(s)}px;line-height:0;white-space:pre;letter-spacing:0;` +
      `transform:translate(${rund(jx)}px,${rund(jy)}px)">${esc(gl.ch)}</span>`;
  });

  const striche = verwackle(box.striche, u, unordnung, index * 3.1 + 11);
  const gn = [];
  for (const s of striche) {
    const p = s.p;
    const css = [];
    for (let i = 0; i < p.length; i += 2) css.push(rund(p[i]), rund(-p[i + 1]));
    gn.push({ p: css, d: rund(s.d) });
    if (p.length === 2) {
      html += balken(p[0], -p[1] + korrStrich, p[0], -p[1] + korrStrich, s.d, farbe);
      continue;
    }
    for (let i = 0; i + 3 < p.length; i += 2) {
      html += balken(p[i], -p[i + 1] + korrStrich, p[i + 2], -p[i + 3] + korrStrich, s.d, farbe);
    }
  }

  // Braucht die Formel mehr Platz als eine Zeile hergibt?
  let randOben = "";
  let stuetzeUnten = "";
  if (opt.zeile) {
    const { L, a } = opt.zeile;
    /* Ein einfacher Bruch ragt etwa eine halbe Zeile nach oben und unten —
       das passt, auch wenn darüber und darunter ebenfalls Brüche stehen.
       Alles, was höher ist (Doppelbruch, Wurzel über Bruch), bekommt eine
       eigene Linie dazu, damit es nicht in die Nachbarzeile ragt. */
    // Zusammen höchstens eine Zeile: dann berühren sich zwei Formeln
    // übereinander nie, auch wenn beide die Grenze ausreizen.
    const obenErlaubt = Math.max(0.56 * L, M.ziffernHoehe * S * 1.2);
    const untenErlaubt = Math.max(0.44 * L, M.unterlaenge * S * 1.2);
    const mehrOben = box.h > obenErlaubt ? Math.ceil((box.h - obenErlaubt) / L) : 0;
    const mehrUnten = box.t > untenErlaubt ? Math.ceil((box.t - untenErlaubt) / L) : 0;
    if (mehrOben) randOben = `margin-top:${rund(a + mehrOben * L)}px;`;
    if (mehrUnten) {
      stuetzeUnten =
        `<span aria-hidden="true" style="display:inline-block;width:0;height:0;` +
        `vertical-align:${rund(-(L - a + mehrUnten * L))}px"></span>`;
    }
  }

  const daten = gn.length ? ` data-gn-striche='${JSON.stringify(gn)}'` : "";
  return (
    `<span class="sb-mathe"${daten} style="display:inline-block;position:relative;` +
    `width:${rund(box.w)}px;height:0;vertical-align:baseline;${randOben}` +
    `line-height:0;white-space:nowrap">${html}</span>${stuetzeUnten}`
  );
}
